import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTheme } from 'common/styling/theme';
import {
  BasicModal,
  DateCell,
  DefaultButton,
  MoneyCell,
  PaginationTable,
  RowLink,
  StatusCell,
  Tooltip,
} from 'common/components';
import { getDynamicStyles, staticStyles } from './style';
import UserBonusWithdrawForm from '../../../users/_components/UserBonusWithdrawForm';

const RecordsBonusesTable = ({
  data,
  loading,
  location,
  history,
  query,
  getList,
  params,
  theme,
  intl,
  profile,
  ...props
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [selectedBonusWithdraw, setSelectedBonusWithdraw] = useState(null);

  const openBonusWithdrawModal = bonus => setSelectedBonusWithdraw(bonus);
  const closeBonusWithdrawModal = () => setSelectedBonusWithdraw(null);

  const convertBonusCodeToText = code => {
    const bonusTypes = [
      {
        value: 'bonus_monthly_interest',
        label: intl.formatMessage({ id: 'bonusMonthlyInterest' }),
      },
      {
        value: 'bonus_welcome',
        label: intl.formatMessage({ id: 'bonusWelcome' }),
      },
      {
        value: 'bonus_on_deposit',
        label: intl.formatMessage({ id: 'bonusOnDeposit' }),
      },
      {
        value: 'bonus_compensation',
        label: intl.formatMessage({ id: 'bonusCompensation' }),
      },
      {
        value: 'bonus_manual',
        label: intl.formatMessage({ id: 'bonusManual' }),
      },
      {
        value: 'bonus_risk_free',
        label: intl.formatMessage({ id: 'bonusRiskFree' }),
      },
    ];

    const res = bonusTypes.find(type => type.value === code);
    return res.label;
  };

  const hasWithdrawBonusPermission = profile.perms
    ? !!profile.perms.withdrawBonuses || !!profile.perms.managerWithdrawBonuses
    : false;

  const renderCancelWithdrawButton = (status, id, submitting, account, value, user) =>
    hasWithdrawBonusPermission && (
      <Tooltip id={`boBonusesTooltip_${id}`} textId={status.toLowerCase() !== 'active' ? 'boBonusesTooltipText' : ''}>
        <DefaultButton
          textId="justWithdraw"
          onClick={e => {
            e.stopPropagation();
            openBonusWithdrawModal({ id, account, value, user });
          }}
          disabled={status.toLowerCase() !== 'active'}
        />
      </Tooltip>
    );

  /* eslint-disable */
  const columns = [
    {
      Header: 'justEmail',
      Cell: ({
        row: {
          original: { user },
        },
      }) => (
        <RowLink link={`/backoffice/users/${user.id}`} className="RowLink__bonuses" isPreviewCell>
          {user.email}
        </RowLink>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'bonusType',
      Cell: ({
        row: {
          original: { bonusType, defaultBonusType },
        },
      }) => (
        <div className="RecordsBonusesTable__types">
          <span>{convertBonusCodeToText(bonusType)}</span>
          <FormattedMessage id={defaultBonusType === 'balance' ? 'justBalance' : 'justCredit'}>
            {txt => <span className="defaultType">{txt}</span>}
          </FormattedMessage>
        </div>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'justAccount',
      Cell: ({
        row: {
          original: { account },
        },
      }) => <span className="RecordsBonusesTable__account">{account}</span>,
    },
    {
      accessor: 'receiving_date',
      Header: 'receivingDate',
      Cell: ({
        row: {
          original: { receivingDate },
        },
      }) => (
        <span className="RecordsBonusesTable__receiving">
          <DateCell value={receivingDate} />
        </span>
      ),
    },
    {
      accessor: 'valid_until',
      Header: 'validUntil',
      Cell: ({
        row: {
          original: { validUntil, status },
        },
      }) =>
        validUntil === '9999-01-01' ? (
          <FormattedMessage id="justEndless" />
        ) : (
          <span className={`RecordsBonusesTable__until ${status.toLowerCase()}`}>
            <DateCell value={validUntil} />
          </span>
        ),
    },
    {
      Header: 'justValue',
      Cell: ({
        row: {
          original: { value },
        },
      }) => <MoneyCell value={value} />,
    },
    {
      Header: 'fulfillmentOfTerms',
      Cell: ({
        row: {
          original: { tradedLots, requiredLots, bonusType },
        },
      }) =>
        !requiredLots ? (
          <span className="RecordsBonusesTable__traded">—</span>
        ) : (
          <span className="RecordsBonusesTable__traded">
            {tradedLots ? `${tradedLots} ${bonusType === 'bonus_risk_free' ? 'USD' : 'Lots'}` : 0}
          </span>
        ),
    },
    {
      Header: 'termsOfReceipt',
      Cell: ({
        row: {
          original: { requiredLots, bonusType },
        },
      }) => (
        <span className="RecordsBonusesTable__required">
          {requiredLots ? `${requiredLots} ${bonusType === 'bonus_risk_free' ? 'USD' : 'Lots'}` : '—'}
        </span>
      ),
    },
    {
      Header: 'justStatus',
      Cell: ({
        row: {
          original: { status },
        },
      }) => <StatusCell statusCode={status} />,
      isPreviewCell: true,
    },
    {
      accessor: 'withdraw_date',
      Header: 'boWithdrawDate',
      Cell: ({
        row: {
          original: { withdrawDate },
        },
      }) => (
        <span className="RecordsBonusesTable__withdraw_date">
          {withdrawDate ? <DateCell value={withdrawDate} /> : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      Header: () => {},
      Cell: ({
        row: {
          original: { status, id, submitting, account, value, user },
        },
      }) => renderCancelWithdrawButton(status, id, submitting, account, value, user),
    },
  ];
  /* eslint-enable */

  return (
    <div className="RecordsBonusesTable">
      <PaginationTable
        {...props}
        data={data}
        columns={columns}
        loading={loading}
        getList={getList}
        location={location}
        history={history}
        params={params}
      />
      <BasicModal captionId="boWithdrawBonus" isOpen={!!selectedBonusWithdraw} onRequestClose={closeBonusWithdrawModal}>
        {!!selectedBonusWithdraw && (
          <UserBonusWithdrawForm
            userId={selectedBonusWithdraw.user.id}
            bonus={selectedBonusWithdraw}
            initialValues={{
              account: selectedBonusWithdraw.account,
              bonus: selectedBonusWithdraw.id,
              chargeValue: selectedBonusWithdraw.value.amount,
              chargeAll: true,
            }}
            onSubmitSuccess={() => {
              closeBonusWithdrawModal();
              getList(params);
            }}
          />
        )}
      </BasicModal>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

RecordsBonusesTable.propTypes = {
  data: PropTypes.object.isRequired,
  columns: PropTypes.array,
  getTrProps: PropTypes.func,
  loading: PropTypes.bool,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  getList: PropTypes.func.isRequired,
  className: PropTypes.string,
  params: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object,
  query: PropTypes.object,
  theme: PropTypes.object,
};

RecordsBonusesTable.defaultProps = {
  getTrProps: () => ({ onClick: () => null }),
  loading: false,
  className: '',
  columns: [],
  query: {},
  theme: {},
  profile: {},
};

export default compose(injectIntl, withTheme())(RecordsBonusesTable);
export { RecordsBonusesTable };
